import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Dialog,
  InputLabel,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DoNotDisturbOnIcon from "@mui/icons-material/DoNotDisturbOn";
import { ReactMultiEmail } from "react-multi-email";
import "react-multi-email/dist/style.css";
import { FormEvent, useEffect, useState } from "react";
import "./EmailModal.scss";
import {
  useGetAddressListQuery,
  useSendEmailMutation,
} from "../../redux/api/email";
import {
  EmailDetails,
  EmailType,
  TemplateType,
} from "../../common/EmailDetails";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../redux/authentication/authSlice";
import { toast } from "react-toastify";
import Editor from "../editor/Editor";
import { $getRoot } from "lexical";
import { $generateHtmlFromNodes } from "@lexical/html";
import { UserGroup } from "../../common/User";

import BasicEmailTemplate from './templates/BasicEmailTemplate';
import MarinerSimpleEmailTemplate from './templates/MarinerSimpleEmailTemplate';
import MarinerTrainingEmailTemplate from './templates/MarinerTrainingEmailTemplate';
import { getEmailContentHTML } from './utils/emailContent';
import { getEmailSignatureHTML } from './utils/emailSignatures';

interface EmailModalProps {
  vesselId?: number | undefined;
  emailData?: { [key: string]: string };
  emailDataList?: { [key: string]: string }[];
  email: string | undefined;
  cc?: string[] | undefined;
  emailType: EmailType;
  uploadedFileUrl?: string | undefined;
  uploadedFileName?: string | undefined;
  children: Element | any;
  parentCallback: Function;
  action?: string;
  jobOrderId?: number;
  trainingOrderId?: number;
  emailSubject?: string;
  closeCallback?: Function;
  jobOrderActions?: { jobOrderId: number; action: string }[];
}

const CONTENT_SIZE_MB = 1024 * 1024;
const ATTACHMENT_MAX_COUNT = 10;
const ATTACHMENT_MAX_MB = 10 * CONTENT_SIZE_MB;

const renderTemplateOptions = (
  emailType: EmailType, 
  template: TemplateType, 
  handleTemplateChange: (templateType: TemplateType) => void
) => {
  if (![
      EmailType.EMAIL_TO_MARINER, 
      EmailType.EMAIL_DOCUMENT_TO_MARINER,
      EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER,
      EmailType.EMAIL_TRAINING_CONTENT_TO_MARINER
    ].includes(emailType)) {
    return null;
  }

  const getTemplateComponent = () => {
    switch (emailType) {
      case EmailType.EMAIL_TO_MARINER:
        return (
          <MarinerSimpleEmailTemplate 
            template={template}
            handleTemplateChange={handleTemplateChange}
          />
        );
      case EmailType.EMAIL_TRAINING_CONTENT_TO_MARINER:
      case EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER:
        return (
          <MarinerTrainingEmailTemplate 
            template={template}
            handleTemplateChange={handleTemplateChange}
          />
        );
      default:
        return (
          <BasicEmailTemplate 
            templateCurrent={template}
            templateName={TemplateType.JOINING_INSTRUCTIONS}
            handleTemplateChange={handleTemplateChange}
          />
        );
    }
  }

  return (
    <div>
      <InputLabel>Template:</InputLabel>
      <BasicEmailTemplate 
        templateCurrent={template}
        templateName={TemplateType.BLANK}
        handleTemplateChange={handleTemplateChange}
      />
      {getTemplateComponent()}
    </div>
  );
}

const EmailModal = (props: EmailModalProps) => {
  const { data: addressList, isLoading: isAddressListLoading } =
    useGetAddressListQuery({});

  const user = useSelector(selectCurrentUser);
  const isMariner = (user?.userGroup as UserGroup) === UserGroup.MARINERS;
  const [emailTo, setEmailTo] = useState<string[]>([
    ...(!!props.email ? [props.email] : []),
  ]);
  const [replyEmail, setReplyEmail] = useState(user?.email);
  const [cc, setCc] = useState<string[]>(
    !!props.cc ? props.cc : []
  );
  const [bcc, setBcc] = useState<string[]>([
    ...(!!user?.email ? [user?.email] : []),
  ]);
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(
    !!props.emailSubject ? props.emailSubject : ""
  );
  const [textBody, setTextBody] = useState("");
  const [htmlBody, setHtmlBody] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState<File[]>();
  const [sendEmail] = useSendEmailMutation();
  const getTemplate = () => {
    switch (props.emailType) {
      case EmailType.EMAIL_TO_TRAVEL_AGENT:
        return TemplateType.TRAVEL_AGENT;
      case EmailType.EMAIL_TO_TRAVEL_AGENT_MULTI:
        return TemplateType.TRAVEL_AGENT_MULTI;
      case EmailType.EVALUATION_ALERT:
        return TemplateType.EVALUATION_REMINDER;
      default:
        return TemplateType.BLANK;
    }
  };
  const [template, setTemplate] = useState<TemplateType>(getTemplate());

  useEffect(() => {
    if (props.emailType === EmailType.EVALUATION_ALERT) {
      setEmailTo(props.email ? [props.email] : []);
    }
  }, [setEmailTo, props.email, props.emailType]);

  useEffect(() => {
    if (
      props.emailType === EmailType.EMAIL_TO_ASSIGNEE &&
      !isAddressListLoading &&
      !!addressList?.crewingEmail &&
      !cc.some((item) => item === addressList?.crewingEmail)
    ) {
      let currentCc = cc;
      currentCc.push(addressList?.crewingEmail);
      setCc(currentCc);
    }
    if (
      props.emailType === EmailType.EMAIL_TO_CREWING &&
      !isAddressListLoading &&
      !!addressList?.crewingEmail &&
      !emailTo.some((item) => item === addressList?.crewingEmail)
    ) {
      let currentEmailTo = emailTo;
      currentEmailTo.push(addressList?.crewingEmail);
      setEmailTo(currentEmailTo);
    }
    if (
      [EmailType.EMAIL_TO_TRAVEL_AGENT, EmailType.EMAIL_TO_TRAVEL_AGENT_MULTI].includes(props.emailType) &&
      !isAddressListLoading &&
      !!addressList?.travelAgentEmail &&
      !emailTo.some((item) => item === addressList?.travelAgentEmail)
    ) {
      let currentEmailTo = emailTo;
      currentEmailTo.push(addressList?.travelAgentEmail);
      setEmailTo(currentEmailTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addressList?.crewingEmail,
    addressList?.travelAgentEmail,
    isAddressListLoading,
    props.emailType,
  ]);

  useEffect(() => {
    if (
      !!props.trainingOrderId &&
      !isAddressListLoading &&
      addressList?.trainingEmail
    ) {
      setBcc([addressList.trainingEmail]);
      setReplyEmail(addressList.trainingEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressList?.trainingEmail, isAddressListLoading, props.trainingOrderId]);

  const initialContentMapping = (templateType: TemplateType) => {
    if (props.emailType === EmailType.EMAIL_TO_CREWING) {
      return "";
    }
    return (
      getEmailContentHTML(
        templateType,
        props.emailData as { [key: string]: string },
        templateType === TemplateType.TRAVEL_AGENT_MULTI ? props.emailDataList : undefined
      ) + getEmailSignatureHTML(user?.name.replaceAll(" ", ""))
    );
  };
  const handleTemplateChange = (templateType: TemplateType) => {
    setTemplate(templateType);
  };

  const handleUploadFiles = (files: File[]) => {
    const uploaded: File[] = !!uploadedFiles ? [...uploadedFiles] : [];
    let limitExceeded = false;
    let toastMessage = "";
    // if the combined filesize of the attachments is > MAX, throw an error
    if (files.reduce((acc, cur) => acc + cur.size, 0) > ATTACHMENT_MAX_MB) {
      toast.error(`You can only upload files with a total combined size of ${
          ATTACHMENT_MAX_MB / CONTENT_SIZE_MB
        } MB.`,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
      return;
    }
    files.forEach((file) => {
      if (uploaded.length === ATTACHMENT_MAX_COUNT) {
        toastMessage = `You can only add a maximum of ${ATTACHMENT_MAX_COUNT} files.`;
      }
      if (toastMessage !== "") {
        limitExceeded = true;
        return true;
      }
      if (uploaded.findIndex((f) => f.name === file.name) === -1) {
        uploaded.push(file);
      }
    });
    if (!limitExceeded) {
      setUploadedFiles(uploaded);
    } else {
      toast.error(toastMessage, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
  };

  const handleFileEvent = (e: any) => {
    const chosenFiles = Array.prototype.slice.call(e.target.files);
    handleUploadFiles(chosenFiles);
  };

  const deleteFile = (fileName: string) => {
    let uploaded: File[] = !!uploadedFiles ? [...uploadedFiles] : [];
    if (uploaded.length > 0) {
      uploaded = uploaded.filter((file) => file.name !== fileName);
      setUploadedFiles(uploaded);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // validate total filesize
    // if the combined filesize of the attachments is > MAX, throw an error
    if (
      !!uploadedFiles &&
      uploadedFiles.reduce((acc, cur) => acc + cur.size, 0) > ATTACHMENT_MAX_MB
    ) {
      toast.error(
        `You can only send files with a total combined size of ${
          ATTACHMENT_MAX_MB / CONTENT_SIZE_MB
        } MB.`,
        {
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );
      return;
    }

    const emailBody: EmailDetails = {
      emailType: props.emailType,
      emailTo: emailTo,
      from: user?.email as string,
      replyTo: replyEmail,
      ccEmail: cc,
      bccEmail: bcc,
      subject: subject,
      textBody: textBody,
      htmlBody: htmlBody,
      uploadedFileUrl:
        [EmailType.EMAIL_DOCUMENT_TO_MARINER, EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER].includes(props.emailType)
          ? props.uploadedFileUrl
          : undefined,
      uploadedFileName:
        [EmailType.EMAIL_DOCUMENT_TO_MARINER, EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER].includes(props.emailType)
          ? props.uploadedFileName
          : undefined,
    };
    uploadDocument(uploadedFiles as File[], emailBody);
  };

  const uploadDocument = async (files: File[], body: EmailDetails) => {
    let response;
    const data: any = new FormData();
    if (!!files && files.length > 0) {
      files.forEach((file) => data.append("files", file));
    }
    // if the EmailModal is called from a Job Order form, send the ID as well to record history
    if (!!props.jobOrderId) {
      body = { ...body, jobOrderId: props.jobOrderId };
      if (!!props.action) {
        body = { ...body, action: props.action };
      }
    }
    // if the EmailModal is called with multiple job orders, record history for each
    if (!!props.jobOrderActions?.length) {
      body = { ...body, jobOrderActions: props.jobOrderActions };
    }
    // if the EmailModal is called from a Training Order form, send the ID as well to record history
    if (!!props.trainingOrderId) {
      body = { ...body, trainingOrderId: props.trainingOrderId };
      if (!!props.action) {
        body = { ...body, action: props.action };
      }
    }
    data.append(
      "email",
      new Blob([JSON.stringify(body)], { type: "application/json" })
    );
    response = await sendEmail(data);
    if (!("error" in response)) {
      setOpen(false);
      props.parentCallback();
    }
  };

  const handleClose = () => {
    setOpen(false);
    if(!!props.closeCallback){
      props.closeCallback();
    }
  }

  return (
    <div>
      <span onClick={() => setOpen(true)}>{props.children}</span>
      <Dialog
        open={open}
        onClose={handleClose}
        className="email-modal"
        id="email-modal"
      >
        <div className="modal-header">
          <Typography variant="h3" id="modal-title">
            New Message
          </Typography>
          <Button onClick={handleClose} className="grey-link-button">
            <CloseIcon />
          </Button>
        </div>
        <form onSubmit={handleSubmit} className="email-form">
          <InputLabel>To:</InputLabel>
          <ReactMultiEmail
            placeholder="To email addresses here"
            emails={emailTo}
            onChange={(_emails: string[]) => {
              setEmailTo(_emails);
            }}
            getLabel={(
              email: string,
              index: number,
              removeEmail: (index: number) => void
            ) => {
              return (
                <div data-tag key={index}>
                  {email}
                  <span data-tag-handle onClick={() => removeEmail(index)}>
                    ×
                  </span>
                </div>
              );
            }}
          />
          <InputLabel>CC:</InputLabel>
          <ReactMultiEmail
            placeholder="CC Email Addresses Here"
            emails={cc}
            onChange={(_emails: string[]) => {
              setCc(_emails);
            }}
            getLabel={(
              email: string,
              index: number,
              removeEmail: (index: number) => void
            ) => {
              return (
                <div data-tag key={index}>
                  {email}
                  <span data-tag-handle onClick={() => removeEmail(index)}>
                    ×
                  </span>
                </div>
              );
            }}
          />
          <InputLabel>BCC:</InputLabel>
          <ReactMultiEmail
            placeholder="BCC Email Addresses Here"
            emails={bcc}
            onChange={(_emails: string[]) => {
              setBcc(_emails);
            }}
            getLabel={(
              email: string,
              index: number,
              removeEmail: (index: number) => void
            ) => {
              return (
                <div data-tag key={index}>
                  {email}
                  <span data-tag-handle onClick={() => removeEmail(index)}>
                    ×
                  </span>
                </div>
              );
            }}
          />
          {!isMariner && (
            <>
              <InputLabel>Reply To:</InputLabel>
              <TextField
                fullWidth
                type="text"
                value={replyEmail}
                onChange={(event) => setReplyEmail(event.target.value)}
              />
            </>
          )}
          <InputLabel>Subject:</InputLabel>
          <TextField
            fullWidth
            type="text"
            value={subject}
            onChange={(event) => setSubject(event.target.value)}
          />
          {renderTemplateOptions(props.emailType, template, handleTemplateChange)}
          <InputLabel>Message:</InputLabel>
          <Editor
            initialContent={initialContentMapping(template)}
            onChange={(state, editor) =>
              state.read(() => {
                setHtmlBody($generateHtmlFromNodes(editor, null));
                setTextBody($getRoot().getTextContent());
              })
            }
          />
          {props.emailType !== EmailType.EVALUATION_ALERT && (
            <div>
              <InputLabel>Attachments:</InputLabel>
              <TextField
                fullWidth
                type="file"
                className="email-attach"
                inputProps={{
                  multiple: true,
                }}
                onChange={(event) => handleFileEvent(event)}
                disabled={uploadedFiles?.length === ATTACHMENT_MAX_COUNT}
              />
            </div>
          )}
          {((!!uploadedFiles && uploadedFiles?.length > 0) ||
            ([EmailType.EMAIL_DOCUMENT_TO_MARINER, EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER].includes(props.emailType)&&
              !!props.uploadedFileName)) && (
            <InputLabel>Uploaded Files:</InputLabel>
          )}
          {[EmailType.EMAIL_DOCUMENT_TO_MARINER, EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER].includes(props.emailType) &&
            !!props.uploadedFileName && (
              <div key={`${props.uploadedFileName as string}`}>
                <IconButton disabled>
                  <DoNotDisturbOnIcon color="disabled" />
                </IconButton>
                {props.uploadedFileName}
              </div>
            )}
          {uploadedFiles?.map((file, index) => (
            <div key={`${file.name}${index}`}>
              <IconButton onClick={() => deleteFile(file.name)}>
                <DeleteIcon color="error" />
              </IconButton>
              {file.name}
            </div>
          ))}
          <div className="modal-footer">
            <Button
              className="outline-button-small"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <input
              className="blue-button-small wd-auto"
              type="submit"
              value="Send"
            />
          </div>
        </form>
      </Dialog>
    </div>
  );
};

export default EmailModal;
